import { Column, defaultWidth } from "@/components/CellGrid/Column";
import { DSCRCell } from "@/components/CellGrid/DSCRCell";
import { defaultLabelColumnWidth } from "@/components/CellGrid/LabelColumn";
import { Row } from "@/components/CellGrid/Row";
import { lineSize } from "@/components/CellGrid/Waterfall";
import { defaultHeight } from "@/components/CellGrid/cell/Wrapper";
import { Doc } from "@/components/Doc/Doc";
import { Icons } from "@/components/Icons/Icons";
import { LabelExplainer } from "@/components/LabelExplainer/LabelExplainer";
import {
  annualizeEpochAtom,
  loanEpochsAtomsContainer,
} from "@/financeModels/owasco/epoch";
import { dscrMolecule } from "@/financeModels/owasco/lookback";
import { Epoch } from "@/financeModels/owasco/types";
import { useIsModelReadOnly } from "@/hooks/useFinancialDeckPermissions";
import { ZIndexOrder } from "@/theme";
import {
  ActionIcon,
  BoxComponentProps,
  Button,
  Divider,
  Group,
  Menu,
  NumberInput,
  Select,
  Stack,
  Text,
} from "@mantine/core";
import { WritableAtom, useAtom, useAtomValue, useSetAtom } from "jotai";
import { memo, useEffect, useState } from "react";
import { BiSolidMessageSquareAdd } from "react-icons/bi";
import { TbClock12 } from "react-icons/tb";
import { css } from "vite-plugin-inline-css-modules";

const { createEpochAtom, deleteEpochAtom, orderedEpochsMolecule } =
  loanEpochsAtomsContainer;

export const EpochRow = ({ ...props }: BoxComponentProps) => {
  const epochAtoms = useAtomValue(orderedEpochsMolecule);
  const dscrAtoms = useAtomValue(dscrMolecule);
  const createEpoch = useSetAtom(createEpochAtom);
  const [isModelReadOnly] = useIsModelReadOnly();

  return (
    <Row
      {...props}
      style={{
        position: "sticky",
        top: 0,
        zIndex: ZIndexOrder.CellGridEpochRow,
        borderBottom: "1px solid #ddd",
      }}
      pl={defaultLabelColumnWidth + lineSize}
      bg={"gray.3"}
    >
      {epochAtoms.map((atom, i) => (
        <Column key={`${atom}`}>
          <DSCRCell
            dscrAtom={dscrAtoms[i]}
            docPath="@/docs/pages/model/historicals/dscr.mdx"
          >
            <CellContent atom={atom} />
          </DSCRCell>
        </Column>
      ))}

      {!isModelReadOnly && (
        <Button
          leftSection={<BiSolidMessageSquareAdd size={22} />}
          h={defaultHeight}
          w={defaultWidth}
          onClick={() => createEpoch()}
          justify="left"
          mx="sm"
          //variant="default"
        >
          Add Period
        </Button>
      )}
    </Row>
  );
};

// For some reason my linter isnt happy moving this into the Cell props?
// So its here....
type CellProps = {
  atom: WritableAtom<Epoch, [Epoch], void>;
};

export const CellContent = memo(
  ({ atom, ...props }: BoxComponentProps & CellProps) => {
    const [isModelShared] = useIsModelReadOnly();
    const [epoch, setEpoch] = useAtom(atom);

    const startYear = String(
      new Date(epoch.duration?.startDate || 0).getFullYear(),
    );
    const endYear = String(
      new Date(epoch.duration?.endDate || 0).getFullYear(),
    );

    const isSameYear = startYear === endYear;
    const yearText = isSameYear
      ? `${startYear}`
      : `${startYear} - ${endYear.toString().replace("20", "")}`;

    return (
      <EditMenu epoch={epoch} onChange={setEpoch}>
        <Group
          className={classes.showEditOnHover}
          gap={0}
          justify="center"
          align="flex-start"
          {...props}
        >
          <Stack gap={0} pos={"relative"}>
            <Group gap={4}>
              <Text fz={"sm"} fw={500} lh={1}>
                {epoch.type === "full" && new Date(epoch.date).getFullYear()}
                {epoch.type === "partial" && `${yearText}`}
                {epoch.type === "trailingTwelveMonths" && `${yearText}`}
              </Text>
              {epoch.type === "full" && epoch.annualized && (
                <TbClock12 size={16} title="Annualized Period" />
              )}

              {!isModelShared && (
                <Button
                  size="compact-sm"
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    width: 86,
                  }}
                  fz={"xs"}
                >
                  Edit Period
                </Button>
              )}
            </Group>
            <Text fz={12} lh={1}>
              {epoch.type === "full" && "Full Year"}
              {epoch.type === "partial" && "Partial Year"}
              {epoch.type === "trailingTwelveMonths" && "Trailing 12 Mo."}
            </Text>
          </Stack>
        </Group>
      </EditMenu>
    );
  },
);

const EditMenu = ({
  epoch,
  onChange,
  children,
}: {
  epoch: Epoch;
  onChange: (epoch: Epoch) => void;
  children: React.ReactNode;
}) => {
  const [opened, setOpened] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const annualizeEpoch = useSetAtom(annualizeEpochAtom);

  const [durationStartYear, setDurationStartYear] = useState(
    epoch.duration
      ? new Date(epoch.duration.startDate).getFullYear()
      : new Date().getFullYear(),
  );

  const [durationEndYear, setDurationEndYear] = useState(
    epoch.duration
      ? new Date(epoch.duration.endDate).getFullYear()
      : new Date().getFullYear(),
  );

  const [durationStartMonth, setDurationStartMonth] = useState(
    epoch.duration ? new Date(epoch.duration.startDate).getMonth() : 0,
  );

  const [durationEndMonth, setDurationEndMonth] = useState(
    epoch.duration ? new Date(epoch.duration.endDate).getMonth() : 0,
  );

  const [epochDraft, setEpochDraft] = useState<Epoch>(epoch);

  // const [yearInputValue, setYearInputValue] = useState(
  //   new Date(epoch.date).getFullYear(),
  // );
  const deleteEpoch = useSetAtom(deleteEpochAtom);

  useEffect(() => {
    setEpochDraft(epoch);
  }, [epoch]);

  useEffect(() => {
    if (epochDraft.duration) return;
    setEpochDraft({
      ...epochDraft,
      duration: { startDate: epochDraft.date, endDate: epochDraft.date },
    });
  }, [epochDraft]);

  const ttmEndDate = new Date(
    durationStartYear,
    durationStartMonth + 11,
    1,
  ).getTime();

  const ttmEndMonth = new Date(ttmEndDate).getMonth();
  const ttmEndYear = new Date(ttmEndDate).getFullYear();

  const onSubmit = (e?: { preventDefault: () => void }) => {
    e?.preventDefault();

    const duration = {
      startDate: new Date(durationStartYear, durationStartMonth, 1).getTime(),
      endDate: new Date(
        epochDraft.type === "trailingTwelveMonths"
          ? ttmEndYear
          : durationEndYear,
        epochDraft.type === "trailingTwelveMonths"
          ? ttmEndMonth
          : durationEndMonth,
        1,
      ).getTime(),
    };

    const draft = {
      ...epochDraft,
      duration,
      date: new Date(duration.startDate).getTime(),
    };

    onChange(draft);
    setOpened(false);
  };

  const onMenuChange = () => {
    setOpened(!opened);
    setShowConfirmDelete(false);

    if (!opened) return;

    onSubmit();
  };

  const onEpochDelete = () => {
    setOpened(!opened);
    deleteEpoch(epoch.id);
  };

  return (
    <Menu
      width={246}
      shadow="md"
      withArrow
      position="bottom"
      opened={opened}
      onChange={onMenuChange}
      //withinPortal={false}
    >
      <Menu.Target>{children}</Menu.Target>

      <Menu.Dropdown>
        {!showConfirmDelete && (
          <form onSubmit={onSubmit}>
            <Stack p="xs" gap={"xs"}>
              <Group justify="space-between">
                <Text fz={"sm"} fw={"bold"}>
                  Edit Period
                </Text>
                <ActionIcon
                  color="red.8"
                  size="sm"
                  onClick={() => {
                    setShowConfirmDelete(true);
                  }}
                  variant="light"
                >
                  <Icons.Delete />
                </ActionIcon>
              </Group>

              <Select
                comboboxProps={{ withinPortal: false }}
                label="Type"
                placeholder="Pick value"
                value={epochDraft.type}
                onChange={
                  (value) =>
                    setEpochDraft({
                      ...epochDraft,
                      type: value as
                        | "full"
                        | "partial"
                        | "trailingTwelveMonths",
                    })
                  // prevent bubbling
                }
                data={[
                  { label: "Full Year (FY)", value: "full" },
                  { label: "Partial Year (PY)", value: "partial" },
                  {
                    label: "Trailing 12 Months (TTM)",
                    value: "trailingTwelveMonths",
                  },
                ]}
                withCheckIcon={false}
                allowDeselect={false}
              />

              {epochDraft.type === "full" && (
                <NumberInput
                  label="Year"
                  min={1}
                  value={durationStartYear}
                  onChange={(value) =>
                    setDurationStartYear(
                      Number(value || new Date().getFullYear()),
                    )
                  }
                />
              )}

              {["trailingTwelveMonths", "partial"].includes(
                epochDraft.type,
              ) && (
                <>
                  <Group>
                    <Select
                      withCheckIcon={false}
                      allowDeselect={false}
                      w={88}
                      comboboxProps={{ withinPortal: false }}
                      label="Start Month"
                      value={String(durationStartMonth)}
                      onChange={(val) => {
                        setDurationStartMonth(Number(val) || 0);
                      }}
                      data={monthsData}
                    />
                    <NumberInput
                      min={1}
                      w={88}
                      label="Start Year"
                      value={durationStartYear}
                      onChange={(value) => {
                        setDurationStartYear(
                          Number(value) || new Date().getFullYear(),
                        );
                      }}
                    />
                    <Select
                      disabled={epochDraft.type === "trailingTwelveMonths"}
                      withCheckIcon={false}
                      allowDeselect={false}
                      w={88}
                      comboboxProps={{ withinPortal: false }}
                      label="End Month"
                      value={
                        epochDraft.type === "trailingTwelveMonths"
                          ? String(ttmEndMonth)
                          : String(durationEndMonth)
                      }
                      onChange={(val) => {
                        setDurationEndMonth(Number(val) || 0);
                      }}
                      data={monthsData}
                    />
                    <NumberInput
                      min={1}
                      disabled={epochDraft.type === "trailingTwelveMonths"}
                      w={88}
                      label="End Year"
                      value={
                        epochDraft.type === "trailingTwelveMonths"
                          ? ttmEndYear
                          : durationEndYear
                      }
                      onChange={(value) => {
                        setDurationEndYear(
                          Number(value) || new Date().getFullYear(),
                        );
                      }}
                    />
                  </Group>
                </>
              )}
              <Group gap={"sm"} mt="sm">
                <Button type="submit" flex={1}>
                  Update Period
                </Button>
              </Group>

              {epochDraft.type === "partial" && (
                <Stack gap={"xs"} align="center">
                  <Divider w={"100%"} />

                  <Button
                    w={"100%"}
                    size="sm"
                    color="yellow"
                    variant="outline"
                    onClick={() => {
                      annualizeEpoch(epochDraft.id);
                      setOpened(false);
                    }}
                  >
                    Annualize Partial Year
                  </Button>

                  <LabelExplainer
                    showIcon
                    label={<Text size="sm">What is Annualizing?</Text>}
                    explanation={
                      <Doc path="@/docs/pages/model/historicals/annualize-partial-year.mdx" />
                    }
                  />
                </Stack>
              )}
            </Stack>
          </form>
        )}
        {showConfirmDelete && (
          <Stack p="xs">
            <Text ta="center">Are you sure? This can't be undone.</Text>
            <Button
              onClick={onEpochDelete}
              color="red"
              leftSection={<Icons.Delete size={20} />}
              fullWidth
            >
              Delete Year
            </Button>
          </Stack>
        )}
      </Menu.Dropdown>
    </Menu>
  );
};

const classes = css`
  .showEditOnHover {
    button {
      opacity: 0;
    }

    &:hover {
      button {
        opacity: 1;
      }
    }
  }
`;

const monthsData = [
  { value: "0", label: "Jan" },
  { value: "1", label: "Feb" },
  { value: "2", label: "Mar" },
  { value: "3", label: "Apr" },
  { value: "4", label: "May" },
  { value: "5", label: "Jun" },
  { value: "6", label: "Jul" },
  { value: "7", label: "Aug" },
  { value: "8", label: "Sep" },
  { value: "9", label: "Oct" },
  { value: "10", label: "Nov" },
  { value: "11", label: "Dec" },
] as const;
